import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  TrainingCourse,
  TrainingAssignment,
  TrainingStatus,
} from '@/models/training.model';

const TRAINING_COURSES_COLLECTION = 'trainingCourses';
const TRAINING_ASSIGNMENTS_COLLECTION = 'trainingAssignments';

export class TrainingService {
  static async getAllCourses(): Promise<TrainingCourse[]> {
    try {
      // Try query with orderBy first (requires index)
      const q = query(
        collection(db, TRAINING_COURSES_COLLECTION),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.docToCourse(doc.id, doc.data()));
    } catch (error: any) {
      // If index error, fall back to query without orderBy
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        console.warn('Firestore index missing, falling back to query without orderBy');
        const q = query(
          collection(db, TRAINING_COURSES_COLLECTION),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const courses = snapshot.docs.map(doc => this.docToCourse(doc.id, doc.data()));
        // Sort manually by createdAt
        return courses.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      }
      throw error;
    }
  }

  static async getCourseById(id: string): Promise<TrainingCourse | null> {
    const docRef = doc(db, TRAINING_COURSES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToCourse(docSnap.id, docSnap.data());
  }

  static async createCourse(courseData: Partial<TrainingCourse>): Promise<string> {
    const now = Timestamp.now();
    const data: DocumentData = {
      title: courseData.title || '',
      description: courseData.description || null,
      type: courseData.type || 'other',
      category: courseData.category || null,
      duration: courseData.duration || 0,
      content: courseData.content || null,
      videoUrl: courseData.videoUrl || null,
      documentUrl: courseData.documentUrl || null,
      quiz: courseData.quiz || null,
      passingScore: courseData.passingScore ?? null,
      isRequired: courseData.isRequired !== undefined ? courseData.isRequired : false,
      isActive: courseData.isActive !== undefined ? courseData.isActive : true,
      tags: courseData.tags || [],
      createdBy: courseData.createdBy || '',
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, TRAINING_COURSES_COLLECTION), data);
    return docRef.id;
  }

  static async updateCourse(id: string, updates: Partial<TrainingCourse>): Promise<void> {
    const docRef = doc(db, TRAINING_COURSES_COLLECTION, id);
    const updateData: Record<string, unknown> = {
      updatedAt: Timestamp.now(),
    };

    // Firestore does not allow undefined values; only set fields that are provided.
    if (updates.title !== undefined) updateData.title = updates.title || '';
    if (updates.description !== undefined) updateData.description = updates.description || null;
    if (updates.type !== undefined) updateData.type = updates.type || 'other';
    if (updates.category !== undefined) updateData.category = updates.category || null;
    if (updates.duration !== undefined) updateData.duration = updates.duration || 0;
    if (updates.content !== undefined) updateData.content = updates.content || null;
    if (updates.videoUrl !== undefined) updateData.videoUrl = updates.videoUrl || null;
    if (updates.documentUrl !== undefined) updateData.documentUrl = updates.documentUrl || null;
    if (updates.quiz !== undefined) updateData.quiz = updates.quiz || null;
    if (updates.passingScore !== undefined) updateData.passingScore = updates.passingScore ?? null;
    if (updates.isRequired !== undefined) updateData.isRequired = updates.isRequired;
    if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
    if (updates.tags !== undefined) updateData.tags = updates.tags || [];

    await updateDoc(docRef, updateData);
  }

  static async getAssignmentsByEmployee(employeeId: string): Promise<TrainingAssignment[]> {
    const q = query(
      collection(db, TRAINING_ASSIGNMENTS_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('assignedDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToAssignment(doc.id, doc.data()));
  }

  static async getAllAssignments(): Promise<TrainingAssignment[]> {
    const q = query(
      collection(db, TRAINING_ASSIGNMENTS_COLLECTION),
      orderBy('assignedDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToAssignment(doc.id, doc.data()));
  }

  static async createAssignment(assignmentData: Partial<TrainingAssignment>): Promise<string> {
    const now = Timestamp.now();
    const data: DocumentData = {
      employeeId: assignmentData.employeeId || '',
      employeeName: assignmentData.employeeName || '',
      courseId: assignmentData.courseId || '',
      courseTitle: assignmentData.courseTitle || '',
      status: assignmentData.status || TrainingStatus.notStarted,
      assignedDate: assignmentData.assignedDate ? Timestamp.fromDate(assignmentData.assignedDate) : now,
      dueDate: assignmentData.dueDate ? Timestamp.fromDate(assignmentData.dueDate) : null,
      startedAt: assignmentData.startedAt ? Timestamp.fromDate(assignmentData.startedAt) : null,
      completedAt: assignmentData.completedAt ? Timestamp.fromDate(assignmentData.completedAt) : null,
      score: assignmentData.score ?? null,
      attempts: assignmentData.attempts || 0,
      assignedBy: assignmentData.assignedBy || '',
      notes: assignmentData.notes || null,
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, TRAINING_ASSIGNMENTS_COLLECTION), data);
    return docRef.id;
  }

  private static docToCourse(id: string, data: DocumentData): TrainingCourse {
    // Handle missing title field - use description or category as fallback
    const title = data.title || data.name || data.description?.substring(0, 50) || 'Untitled Course';
    
    return {
      id,
      title,
      description: data.description,
      type: data.type || 'other',
      category: data.category,
      duration: data.duration || 0,
      content: data.content,
      videoUrl: data.videoUrl,
      documentUrl: data.documentUrl,
      quiz: data.quiz,
      passingScore: data.passingScore,
      isRequired: data.isRequired || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      tags: data.tags || [],
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  private static docToAssignment(id: string, data: DocumentData): TrainingAssignment {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      courseId: data.courseId || '',
      courseTitle: data.courseTitle || '',
      status: data.status || TrainingStatus.notStarted,
      assignedDate: data.assignedDate?.toDate() || new Date(),
      dueDate: data.dueDate?.toDate(),
      startedAt: data.startedAt?.toDate(),
      completedAt: data.completedAt?.toDate(),
      score: data.score,
      attempts: data.attempts || 0,
      assignedBy: data.assignedBy || '',
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }
}

